import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import Button from "../../components/utils/Button";
import CardLayout from "../../components/utils/CardLayout";
import Table from "../../components/utils/Table";
import genericActions from "../../redux/generic/actions";
import { useDispatch, useSelector } from "react-redux";
import ActivationActions from "../../redux/activation/actions";
import { twoDigitDecimal } from "../../helpers/commonFunction";
import ToastAlert from "../../components/utils/ToastAlert";
import authActions from "../../redux/auth/actions";

const { getPlan, getBillingCode } = genericActions;

const { createBulkActivation, resetMessage } = ActivationActions;
const { getUserDataByToken } = authActions;

const MAX_RECORDS = 100;

const BulkActivation = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [previewData, setPreviewData] = useState([]);
  const [vailidError, setVailidError] = useState("");
  const [previewColumn, setPreviewColumn] = useState([]);
  const { planList, getPlanLoading, billingCodeList } = useSelector((state) => state.generic);
  const { bulkActivationFResponse, bulkActivationLoading, bulkActivationMessage, bulkActivationError } = useSelector((state) => state.activation);
  const [selectedPlan, setSelectedPlan] = useState({})
  const [invailidData, setInvailidData] = useState(0);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors }, } = useForm({
    mode: "onChange",
    defaultValues: {
      planId: "",
      billingCode: ''
    },
  });

  const watchPlanId = watch('planId');

  useEffect(() => {
    if (bulkActivationMessage) {
      ToastAlert({ type: "success", text: bulkActivationMessage });
      handleReset();
      dispatch(getUserDataByToken());
    }

    if (bulkActivationError) {
      ToastAlert({ type: "error", text: bulkActivationError });
    }
  }, [bulkActivationMessage, bulkActivationError]);


  useEffect(() => {
    dispatch(getPlan({ ActionType: 1, Month: 1 }))
    dispatch(getBillingCode({ ActionType: 1 }))
    return () => {
      dispatch(resetMessage());
    };

  }, []);

  useEffect(() => {
    if (billingCodeList && billingCodeList?.length > 0) {
      setValue('billingCode', billingCodeList[0]?.BillingCode)
    }
  }, [billingCodeList])

  const downloadSampleFile = () => {
    const data = [
      {
        "SerialNumber": '1234567890123456789',
        "ZipCodeForAreaCode": '12345',
        "Street1": 'New York',
        "Street2": 'St2',
        'City': 'Test City',
        'State': 'Test State',
        "ZipCode": '123456',
      }

    ];
    downloadExcelSampleFile(data, "bulk-activation-sample-file.xlsx");
  }

  const onFileUpload = (e) => {
    const fileReader = new FileReader();
    let file = e.target.files[0];
    setVailidError("");
    setPreviewColumn([]);
    setPreviewData([]);
    setInvailidData(0);
    if (!file) return;
    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setVailidError("Please upload a valid file (.xlsx, .xls)");
      return;
    }

    fileReader.readAsArrayBuffer(file);
    fileReader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      // Convert to JSON (rows)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        defval: "", // avoid undefined
      });

      // Get columns (headers)
      const headers = Object.keys(jsonData[0] || {});
      const requiredColumns = ["SerialNumber", "ZipCodeForAreaCode", "Street1", "Street2", 'City', 'State', "ZipCode"];
      const isValid =
        headers.length === 7 && requiredColumns.every((col) => headers.includes(col));
      if (!isValid) {
        setVailidError("Invalid file. required columns are missing or incorrect.",);
        return;
      }

      if (jsonData?.length > MAX_RECORDS) {
        setVailidError(`Maximum ${MAX_RECORDS} records allowed.`);
        return;
      }

      const heading = [
        ...headers.map((id) => ({ displayName: `${id}`, key: `${id}` })),
        { displayName: "Remarks", key: "remark" },
        { displayName: "Status", key: "status", textAlign: true },
      ];

      setPreviewColumn(heading);
      const validatedData = validateData(jsonData) || [];
      const invailidDataFilter = validatedData.filter((item) => item?.validFlag == 0);
      setInvailidData(invailidDataFilter?.length);

      setPreviewData(validatedData);

    };

  };

  const validateData = (rows) => {
    const serialSeen = new Set();
    return rows.map((row) => {
      let remark = [];
      let validFlag = 1;
      const serial = String(row['SerialNumber'] || "").trim();
      const areaCode = String(row['ZipCodeForAreaCode'] || "").trim();
      const street1 = String(row['Street1'] || "").trim();
      const city = String(row['City'] || "").trim();
      const state = String(row['State'] || "").trim();
      const zipcode = String(row['ZipCode'] || "").trim();
      // ["SerialNumber",'ZipCodeForAreaCode', "Street1", "Street2", 'City', 'State', "ZipCode"];

      if (!serial) {
        remark.push("Serial number is missing");
        validFlag = 0;
      }
      else if (!areaCode) {
        remark.push("ZipCodeForAreaCode is missing");
        validFlag = 0;
      }
      else if (!zipcode) {
        remark.push("ZipCode is missing");
        validFlag = 0;
      }
      else if (!street1) {
        remark.push("Street1 is missing");
        validFlag = 0;
      }
      else if (!city) {
        remark.push("City is missing");
        validFlag = 0;
      }
      else if (!state) {
        remark.push("State is missing");
        validFlag = 0;
      }

      else if (!/^\d+$/.test(serial)) {
        remark.push("Serial number must be numeric");
        validFlag = 0;
      } else if (serial.length !== 19) {
        remark.push("Serial number must be 19 digits");
        validFlag = 0;
      } else if (serialSeen.has(serial)) {
        remark.push("Duplicate serial number");
        validFlag = 0;
      } else {
        serialSeen.add(serial);
      }

      return {
        ...row,
        SerialNumber: serial,
        validFlag,
        remark: remark.length ? remark.join(", ") : "Valid",
      };
    });

  };

  const rowData = useMemo(() => {
    if (previewData && previewData.length > 0) {
      return previewData.map((item) => ({
        ...item,
        status:
          item.validFlag === 1 ? (
            <div className='text-success d-flex align-items-center justify-content-center'>
              <i className="ri-checkbox-circle-fill fs-6"></i> <span className="fw-semibold">Valid</span>
            </div>
          ) : (
            <div className="text-danger d-flex align-items-center justify-content-center">
              <i className="ri-close-circle-fill fs-6"></i><span className="fw-semibold">Invalid</span>
            </div>
          ),
      }));

    }
    return [];

  }, [previewData]);

  useEffect(() => {
    if (!watchPlanId || !planList?.length) {
      setSelectedPlan(null);
      return;
    }

    const planData = planList.find(
      (item) => item?.PlanID == watchPlanId
    );

    if (!planData) {
      setSelectedPlan(null);
      return;
    }

    const planAmount = Number(planData?.PlanAmount || 0);
    const dealerCostAmount = Number(planData?.DealerCost || 0);
    const quantity = Number(rowData?.length || 0) - Number(invailidData || 0);
    const totalAmount = quantity * planAmount;
    const totalDealerCost = quantity * dealerCostAmount;

    setSelectedPlan({
      ...planData,
      totalAmount,
      totalDealerCost,
    });
  }, [watchPlanId, planList, rowData]);

  const onSubmit = (data) => {
    const simDetails = rowData && rowData.length > 0 && rowData.map((item) => {
      return {
        "iccid": item?.SerialNumber,
        "zipcode": String(item?.ZipCode),
        "street1": item?.Street1,
        "street2": item?.Street2 || '',
        "city": item?.City,
        "state": item?.State,
        "areaCode": String(item?.ZipCodeForAreaCode),
      };
    });

    const payload = {
      ...data,
      "planId": Number(data?.planId) || 0,
      requestType: 2,
      sims: simDetails
    }

    dispatch(createBulkActivation(payload))

  };

  const handleReset = () => {
    reset({ planId: "", });
    setVailidError("");
    setPreviewColumn([]);
    setPreviewData([]);
    setInvailidData(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const fResultColumn = [
    { displayName: "Serial Number", key: "serialNumber" },
    { displayName: "Remark", key: "remark" },
    { displayName: "Status", key: "status", textAlign: true },
  ];

  const fRowData = useMemo(() => {
    return Array.isArray(bulkActivationFResponse) && bulkActivationFResponse.length > 0
      ? bulkActivationFResponse.map((item, key) => {
        return {
          key: key + 1,
          serialNumber: item?.serialNumber,
          status: <div style={{ textAlign: "center" }}>
            <i className="ri-close-circle-fill text-danger fs-5"></i>
          </div>,
          remark: item?.remarks,
        };
      })
      : [];
  }, [bulkActivationFResponse]);

  const handleBack = () => {
    dispatch(resetMessage())
  }

  const downloadResponseFile = () => {
    const data = bulkActivationFResponse && bulkActivationFResponse.length > 0 && bulkActivationFResponse.map((item, ind) => (
      {
        "Serial Number": item?.serialNumber,
        "Remark": item?.remarks,
        "Status": "Invalid"
      }));

    downloadExcelSampleFile(data, "bulk-activation-response-file.xlsx");
  }


  return (
    <CardLayout title="Bulk Activation" icon="fas fa-layer-group"
      rightContent={
        bulkActivationFResponse && bulkActivationFResponse.length > 0 ?
          <div className="d-flex gap-2">
            <Button
              title="Back"
              buttonType="button"
              icon={<i className="ri-arrow-left-line me-1" />}
              variant="primary"
              style={{ padding: "5px 10px", }}
              onClick={() => handleBack()}
            />
            <Button
              title="Excel"
              buttonType="button"
              icon={<i className="ri-download-cloud-line me-1" />}
              variant="secondary"
              style={{ padding: "5px 10px", }}
              onClick={downloadResponseFile}
            />
          </div>
          : <Button
            title="Sample File"
            buttonType="button"
            icon={<i className="ri-download-cloud-line me-1" />}
            variant="secondary"
            style={{
              padding: "4px 10px",
            }}
            onClick={downloadSampleFile}
          />
      }>
      {bulkActivationFResponse && bulkActivationFResponse.length > 0 ?
        <div>
          <div className="mt-1">
            <Table
              columns={fResultColumn}
              loading={false}
              data={fRowData}
              pagination={true}
            />
          </div>
        </div> : <>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              {/* Plan */}
              <div className="col-lg-3 col-md-6 mb-3">
                <label className="form-label fw-semibold">
                  Plan <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  {...register("planId", {
                    required: "Plan is required",
                  })}
                  disabled={getPlanLoading}
                >
                  <option value="">Select Plan</option>
                  {planList?.map((item) => (
                    <option key={item?.PlanID} value={item?.PlanID}>
                      {item?.PlanName}
                    </option>
                  ))}
                </select>
                {errors.planId && <small className="text-danger">{errors.planId.message}</small>}
              </div>

              {/* Billing Code */}
              {/* <div className="col-lg-3 col-md-6 mb-3">
                <label className="form-label fw-semibold">Billing Code <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  {...register("billingCode", {
                    required: "Billing code is required",
                  })}
                >
                  <option value="">Select Billing Code</option>
                  {billingCodeList && billingCodeList.length > 0 && billingCodeList.map((item) => (
                    <option key={item?.BillingCode} value={item?.BillingCode}>
                      {item?.BillingCode}
                    </option>
                  ))}

                </select>
                {errors.billingCode && <small className="text-danger">{errors.billingCode.message}</small>}
              </div> */}

              <div className="form-group col-lg-3 col-md-4">
                <label className="form-label fw-semibold">
                  Upload File <span className="text-danger">*</span>
                  <span
                    className="position-relative ms-2 info-icon-wrapper"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fas fa-info-circle text-primary fs-5 info-icon-text" />

                    <div className="dealer-confirm-popup position-absolute bg-white border rounded shadow-sm py-2">
                      <ul className="mb-0 ps-4 custom-info-list">
                        <li className="mb-1"> Maximum {MAX_RECORDS} records are allowed per submission. </li>
                        <li className="mb-1"> Duplicate Serial Numbers are not allowed. </li>
                        <li className="mb-1"> All fields are required. </li>
                      </ul>
                    </div>
                  </span>
                </label>

                {/* Hidden File Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls"
                  className="form-control"
                  id="fileUpload"
                  onChange={onFileUpload}
                />
                {vailidError && (<div className="" style={{ color: "#dc3545", fontSize: "12px", marginTop: "2px", }} > {vailidError} </div>)}
              </div>

              {/* Actions */}
              <div className="col-md-2 d-flex align-items-end" style={{ height: '70px' }}>
                <div className="d-flex gap-3">
                  <Button
                    title="Activate"
                    buttonType="submit"
                    variant="primary"
                    loading={bulkActivationLoading}
                    icon={<i className="ri-send-plane-fill me-2"></i>}
                    disable={(invailidData > 0) || rowData?.length == 0}
                  />

                  <Button
                    title="Reset"
                    buttonType="button"
                    variant="secondary"
                    disable={bulkActivationLoading}
                    icon={<i className="ri-refresh-line me-2"></i>}
                    onClick={handleReset}
                  />
                </div>
              </div>
            </div>
          </form>

          <div className="mt-3">
            <div className="row g-3 mb-3 justify-content-start">

              {/* Plan Price */}
              {/* <div className="col-12 col-sm-6 col-lg-2">
                <div className="d-flex align-items-center border rounded-3 px-2 py-2 bg-white shadow-sm h-100">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center btn-primary-light bg-opacity-10 rounded-3"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <i className="fa-solid fa-tag text-primary"></i>
                    </div>

                    <div>
                      <div className="text-muted small">Plan Price <span style={{ fontSize: "10px", fontWeight: '600', color: "#212529bf" }}>/ unit</span></div>
                      <div className="text-primary fw-bold">
                        ${twoDigitDecimal(selectedPlan?.PlanAmount)}
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Plan Details */}
              {selectedPlan?.PlanDesc && <div className="col-12 col-sm-6 col-lg-2">
                <div className="d-flex align-items-center border rounded-3 px-2 py-2 bg-white shadow-sm h-100 position-relative info-icon-wrapper" style={{ cursor: "pointer", }}>
                  <div className="d-flex align-items-center gap-3">
                    <div className=""
                    >
                      <div className="text-muted small">Plan Description</div>
                      <div className="text-primary fw-semibold" style={{ fontSize: "13px", }}>
                        <div

                        >
                          {selectedPlan?.PlanDesc?.length > 20 ? `${selectedPlan.PlanDesc.slice(0, 20)}...` : selectedPlan?.PlanDesc}

                          <div className="dealer-confirm-popup activation-popup position-absolute bg-white border rounded shadow-sm py-2" style={{ marginLeft: '-10px' }}>
                            <div className="mb-0 ps-4 custom-info-list">
                              {selectedPlan?.PlanDesc}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

              {selectedPlan?.PlanCode && <div className="col-12 col-sm-6 col-lg-2">
                <div className="d-flex align-items-center border rounded-3 px-2 py-2 bg-white shadow-sm h-100">
                  <div className="d-flex align-items-center gap-3">
                    <div className=""
                    >
                      <div className="text-muted small">SKU</div>
                      <div className="text-primary fw-semibold" style={{ fontSize: "13px", }}>
                        {selectedPlan?.PlanCode}
                      </div>
                    </div>
                  </div>
                </div>
              </div>}

              {rowData && rowData.length > 0 && <>
                {/* Total Quantity */}
                <div className="col-12 col-sm-6 col-lg-2">
                  <div className="d-flex align-items-center border rounded-3 px-2 py-2 bg-white shadow-sm h-100">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="d-flex align-items-center justify-content-center bg-warning bg-opacity-10 rounded-3"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <i className="fa-solid fa-boxes-stacked text-warning"></i>
                      </div>

                      <div>
                        <div className="text-muted small">Total Quantity</div>
                        <div className="text-warning fw-bold">
                          {rowData?.length ?? 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Valid/InValid */}
                <div className="col-12 col-sm-6 col-lg-2">
                  <div className="border rounded-3 px-2 py-2 bg-white shadow-sm h-100">
                    <div className="d-flex align-items-center justify-content-between h-100">

                      {/* Valid */}
                      <div className="text-center flex-fill">
                        <div className="text-muted small">Valid</div>
                        <div className="text-success fw-bold fs-5">{Math.max(0, (rowData?.length || 0) - (invailidData || 0))}</div>
                      </div>

                      {/* Divider */}
                      <div
                        className="border-start mx-2"
                        style={{ height: "35px" }}
                      ></div>

                      {/* Invalid */}
                      <div className="text-center flex-fill">
                        <div className="text-muted small">Invalid</div>
                        <div className="text-danger fw-bold fs-5">{invailidData}</div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Total Plan Price */}
                {/* <div className="col-12 col-sm-6 col-lg-2">
                <div className="d-flex align-items-center border rounded-3 px-2 py-2 bg-white shadow-sm h-100">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center bg-info bg-opacity-10 rounded-3"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <i className="fa-solid fa-sack-dollar text-info"></i>
                    </div>

                    <div>
                      <div className="text-muted small">Total Plan Price</div>
                      <div className="text-info fw-bold">
                        ${twoDigitDecimal(selectedPlan?.totalAmount)}
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}

                {/* Dealer Cost */}
                <div className="col-12 col-sm-6 col-lg-2">
                  <div className="d-flex align-items-center border rounded-3 px-2 py-2 bg-white shadow-sm h-100">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="d-flex align-items-center justify-content-center btn-secondary-light bg-opacity-10 rounded-3"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <i className="fa-solid fa-hand-holding-dollar text-success"></i>
                      </div>

                      <div>
                        <div className="text-muted small">Dealer Cost <span style={{ fontSize: "10px", fontWeight: '600', color: '#212529bf' }}>/ unit</span></div>
                        <div className="text-success fw-bold">
                          ${twoDigitDecimal(selectedPlan?.DealerCost)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total Dealer Cost */}
                <div className="col-12 col-sm-6 col-lg-2">
                  <div className="d-flex align-items-center border rounded-3 px-2 py-2 bg-white shadow-sm h-100">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 rounded-3"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <i className="fa-solid fa-money-bill-trend-up text-danger"></i>
                      </div>

                      <div>
                        <div className="text-muted small">Total Dealer Cost</div>
                        <div className="text-danger fw-bold">
                          ${twoDigitDecimal(selectedPlan?.totalDealerCost)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>}

            </div>

            <Table
              columns={previewColumn}
              loading={false}
              data={rowData}
              pagination={true}
              tableHeight="210px"
            />
          </div>
        </>
      }
    </CardLayout>

  );

};

export default BulkActivation;